import React from 'react';
import { Button } from 'flowbite-react';
import router from 'next/router';
import { useMutation } from '@apollo/client';

import ChevronRight from '@/components/commons/icons/ChevronRight';
import PanelContainer from '@/components/commons/panel_container';
import routes from '@/services/routes';
import { UPDATE_USER } from '@/components/onboarding/Actions';
import { BIO_STATES, BIO_NAMES } from '@/components/onboarding/Shared';
import { type bioPageProps } from '@/types/globals';

import BioPageHeader from './BioPageHeader';

function CareerHistoryPanel({
    isModal = false,
    isEdit,
    extendContinue = null,
}: Omit<bioPageProps, 'overrideBack'>) {
    const [updateUser] = useMutation(UPDATE_USER);

    const handleSkip = async () => {
        if (isModal && extendContinue) {
            extendContinue('skip');
        } else {
            const attributes = {
                onboardingState: BIO_STATES.editBio,
            };
            await updateUser({
                variables: {
                    updateUserInput: {
                        attributes,
                    },
                },
            });
            router.push(routes.BIO_EDIT_BIO);
        }
    };

    return (
        <PanelContainer stepNames={BIO_NAMES} paddingOverride="p-0 sm:p-8" step={1} totalSteps={5} isModal={isModal}>
            <div className="w-full p-0 sm:p-6">
                <BioPageHeader
                    header="Now, let&apos;s talk about your experience."
                    subHeader="To help Sixty's AI write a compelling first draft, we'll need some details about your career. You can share this information in two ways: by sending us something you've already created (LinkedIn or a resume) or by describing it in your own words."
                    subHeaderWidth="max-w-[665px]"
                />
                <div className="flex flex-col md:flex-row space-between gap-8">
                    <div className="w-full md:w-1/2 border border-gray-300 shadow-md rounded-md  flex flex-col justify-center">
                        <h1 className="pt-0 sm:pt-8 pr-0 sm:pr-8 pl-0 sm:pl-8 text-center text-[22px] font-semibold font-instrument"> Import your career history</h1>
                        <p className="text-center pr-1 sm:pr-8 pl-1 sm:pl-8 pt-2 pb-10 text-base font-instrument">Upload a recognized file format that our AI can read.</p>
                        <ul className="flex flex-col px-3 sm:px-10 font-instrument">
                            <li className="pb-4 flex gap-2 font-instrument">
                                <img src="/images/linked_in.svg" alt="star" />
                                <p className="font-instrument">
                                    Data file from LinkedIn
                                </p>

                            </li>
                            <li className="pb-4 flex gap-2 font-instrument">
                                <img src="/images/pdf.svg" alt="star" />
                                <p className="font-instrument">Resume or other document (PDF)</p>
                            </li>
                            <li className="pb-8 flex gap-2 font-instrument">
                                <img src="/images/bars.svg" alt="star" />
                                <p className="font-instrument">Cut and paste text from another source</p>
                            </li>
                        </ul>
                        <div className="px-4 sm:px-10 pb-6 mt-auto">
                            <Button
                                className="font-instrument w-full text-cirrusWhite mx-auto bg-gray-700 hover:bg-gray-900 focus:outline-none focus:ring-4 focus:ring-gray-300 font-medium rounded-lg text-sm mb-2 dark:bg-gray-800 dark:hover:bg-gray-700 dark:focus:ring-gray-700 dark:border-gray-700"
                                onClick={() => {
                                    // Don't update user
                                    // onboardingState for this interstitual screen
                                    if (isModal && extendContinue) {
                                        extendContinue('import');
                                    } else {
                                        router.push(routes.BIO_SELECT_IMPORT_METHOD);
                                    }
                                }}
                            >
                                Import file(s)
                            </Button>
                        </div>
                    </div>
                    <div className="w-full md:w-1/2 border border-gray-300 shadow-md rounded-md flex flex-col justify-center">
                        <h1 className="pt-0 sm:pt-8 pr-0 sm:pr-8 pl-0 sm:pl-8 text-center text-[22px] font-semibold font-instrument">
                            Summarize your career history
                        </h1>
                        <p className="text-center pr-0 sm:pr-8 pl-0 sm:pl-8 pt-2 pb-10 text-base font-instrument">
                            Write brief snippets about your career that our AI can read.
                        </p>
                        <ul className="flex flex-col px-3 sm:px-10 ">
                            <li className="pb-4 flex gap-2">
                                <img src="/images/message_blurb.svg" alt="star" />
                                <p className="font-instrument">
                                    Describe each job in your own words
                                </p>
                            </li>
                            <li className="pb-4 flex gap-2">
                                <img src="/images/star-outline.svg" alt="star" />
                                <p className="font-instrument">Emphasize your highlights and achievements</p>
                            </li>
                            <li className="pb-8 flex gap-2">
                                <img src="/images/list.svg" alt="star" />
                                <p className="font-instrument">Include as many or as few as you like</p>
                            </li>
                        </ul>
                        <div className="px-4 sm:px-10 pb-6 mt-auto">
                            <Button
                                className="font-instrument w-full text-cirrusWhite mx-auto bg-gray-700 hover:bg-gray-900 focus:outline-none focus:ring-4 focus:ring-gray-300 font-medium rounded-lg text-sm mb-2 dark:bg-gray-800 dark:hover:bg-gray-700 dark:focus:ring-gray-700 dark:border-gray-700"
                                onClick={() => {
                                    // Don't update user onboardingState
                                    // for this interstitual screen
                                    if (isModal && extendContinue) {
                                        extendContinue('', isEdit);
                                    } else {
                                        router.push(routes.BIO_MANUAL_CAREER_HISTORY);
                                    }
                                }}
                            >
                                Add current or recent job
                            </Button>
                        </div>
                    </div>

                </div>
            </div>
            <div className="w-full flex justify-end pt-10 text-sm text-progressBarBlue">
                <Button
                    className="hover:bg-primary sm:mr-[26px] block text-primary border-gray-300 border-y border-x w-[220px] h-10 bg-white py-0 px-0"
                    onClick={handleSkip}
                >
                    <ChevronRight className="ml-3 mr-2" />
                    Skip this for now
                </Button>
            </div>
        </PanelContainer>
    );
}

export default CareerHistoryPanel;
